// Shared helpers for the public menu and the admin screens.

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Full class strings so Tailwind keeps them in the build.
export const TAG_COLORS = {
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200',
  red: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-200',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200',
  lime: 'bg-lime-100 text-lime-800 dark:bg-lime-900/50 dark:text-lime-200',
  green: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200',
  teal: 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 dark:text-teal-200',
  sky: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200',
  indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200',
  purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/50 dark:text-fuchsia-200',
  pink: 'bg-pink-100 text-pink-800 dark:bg-pink-900/50 dark:text-pink-200',
};
export const TAG_COLOR_NAMES = Object.keys(TAG_COLORS);
export const tagClass = color => TAG_COLORS[color] || TAG_COLORS.slate;

export function formatPrice(price) {
  if (price === null || price === undefined || price === '') return '';
  const n = Number(price);
  if (!Number.isFinite(n)) return String(price);
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

// "16:00" -> "4PM", "16:30" -> "4:30PM"
export function formatTime(hhmm) {
  const [h, m] = String(hhmm || '0:0').split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return m ? `${hour}:${String(m).padStart(2, '0')}${suffix}` : `${hour}${suffix}`;
}

export function formatTimeRange(start, end) {
  const s = formatTime(start);
  const e = formatTime(end);
  // "4PM-7PM" reads better as "4-7PM" when both share AM/PM
  if (s.slice(-2) === e.slice(-2)) return `${s.slice(0, -2)}-${e}`;
  return `${s}-${e}`;
}

// [1,2,3,4,5] -> "Mon-Fri", [0,6] -> "Sun, Sat", all -> "Daily"
export function formatDays(days) {
  const sorted = [...new Set(days)].sort((a, b) => a - b);
  if (sorted.length === 7) return 'Daily';
  const runs = [];
  for (const d of sorted) {
    const last = runs[runs.length - 1];
    if (last && d === last[1] + 1) last[1] = d;
    else runs.push([d, d]);
  }
  return runs
    .map(([a, b]) => (a === b ? DAY_NAMES[a] : b === a + 1 ? `${DAY_NAMES[a]}, ${DAY_NAMES[b]}` : `${DAY_NAMES[a]}-${DAY_NAMES[b]}`))
    .join(', ');
}

// Day of week and minutes since midnight in the venue's time zone.
function venueNow(timeZone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'America/New_York',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = type => parts.find(p => p.type === type)?.value;
    return { day: DAY_NAMES.indexOf(get('weekday')), minutes: Number(get('hour')) * 60 + Number(get('minute')) };
  } catch {
    const d = new Date();
    return { day: d.getDay(), minutes: d.getHours() * 60 + d.getMinutes() };
  }
}

const toMinutes = hhmm => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + m;
};

// Handles windows that cross midnight (e.g. 22:00-02:00 counts the early
// hours toward the previous day).
export function isHappyHourNow(hh, timeZone) {
  const { day, minutes } = venueNow(timeZone);
  const start = toMinutes(hh.start_time);
  const end = toMinutes(hh.end_time);
  if (start <= end) return hh.days.includes(day) && minutes >= start && minutes < end;
  const yesterday = (day + 6) % 7;
  return (hh.days.includes(day) && minutes >= start) || (hh.days.includes(yesterday) && minutes < end);
}

export function distanceFeet(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(a)) * 3.28084;
}

export function formatDistance(feet) {
  if (feet < 1000) return `${Math.round(feet)} ft`;
  return `${(feet / 5280).toFixed(feet < 52800 ? 1 : 0)} miles`;
}

// Promise wrapper around the browser location prompt.
export function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(Object.assign(new Error('unsupported'), { code: 'unsupported' }));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 });
  });
}

export function summarizeHappyHour(hh) {
  return `${formatDays(hh.days)} ${formatTimeRange(hh.start_time, hh.end_time)}`;
}
